import { Link } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface PaginationMetadata {
  count: number
  page: number
  limit: number
  pages: number
  previous: number | null
  next: number | null
  /** Exact row bounds for group-aware pagination; falls back to limit math. */
  from?: number
  to?: number
  /** True when an unusually large day is intentionally split at the safety cap. */
  split_day?: boolean
}

interface PaginationProps {
  pagination: PaginationMetadata
  baseUrl: string
  className?: string
  pageParam?: string
}

export function Pagination({
  pagination,
  baseUrl,
  className,
  pageParam = "page",
}: PaginationProps) {
  if (pagination.pages <= 1) {
    return null
  }

  const buildUrl = (page: number) => {
    const currentParams = new URLSearchParams(
      typeof window !== "undefined" ? window.location.search : "",
    )
    currentParams.set(pageParam, String(page))
    const queryString = currentParams.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = []
    const current = pagination.page
    const total = pagination.pages

    pages.push(1)

    if (current > 3) {
      pages.push("ellipsis")
    }

    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      if (!pages.includes(i)) {
        pages.push(i)
      }
    }

    if (current < total - 2) {
      pages.push("ellipsis")
    }

    if (total > 1 && !pages.includes(total)) {
      pages.push(total)
    }

    return pages
  }

  return (
    <nav
      className={cn("flex items-center justify-center gap-1", className)}
      aria-label="Pagination"
    >
      <Button
        variant="outline"
        size="sm"
        disabled={!pagination.previous}
        asChild={!!pagination.previous}
      >
        {pagination.previous ? (
          <Link
            href={buildUrl(pagination.previous)}
            preserveScroll
            preserveState
            prefetch
            cacheFor="30s"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous</span>
          </Link>
        ) : (
          <span>
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous</span>
          </span>
        )}
      </Button>

      {getPageNumbers().map((pageNum, idx) =>
        pageNum === "ellipsis" ? (
          <span key={`ellipsis-${idx}`} className="text-muted-foreground px-2">
            ...
          </span>
        ) : (
          <Button
            key={pageNum}
            variant={pageNum === pagination.page ? "default" : "outline"}
            size="sm"
            asChild={pageNum !== pagination.page}
          >
            {pageNum === pagination.page ? (
              <span>{pageNum}</span>
            ) : (
              <Link
                href={buildUrl(pageNum)}
                preserveScroll
                preserveState
                prefetch
                cacheFor="30s"
              >
                {pageNum}
              </Link>
            )}
          </Button>
        ),
      )}

      <Button
        variant="outline"
        size="sm"
        disabled={!pagination.next}
        asChild={!!pagination.next}
      >
        {pagination.next ? (
          <Link
            href={buildUrl(pagination.next)}
            preserveScroll
            preserveState
            prefetch
            cacheFor="30s"
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next</span>
          </Link>
        ) : (
          <span>
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next</span>
          </span>
        )}
      </Button>
    </nav>
  )
}
